<template>
  <section class="admin-nps-page">
    <div class="bb-container">

      <div class="g-edit__header">
        <h1 class="g-edit__title">NPS Dashboard</h1>
      </div>

      <!-- Filtros -->
      <div class="nps-filters">
        <select v-model="filters.days" class="bb-form nps-filters__select" @change="loadStats">
          <option value="7">Últimos 7 días</option>
          <option value="30">Últimos 30 días</option>
          <option value="90">Últimos 90 días</option>
          <option value="0">Todo el tiempo</option>
        </select>

        <select v-model="filters.country" class="bb-form nps-filters__select" @change="loadStats">
          <option value="">Todos los países</option>
          <option value="ES">España</option>
          <option value="IT">Italia</option>
          <option value="FR">Francia</option>
          <option value="DE">Alemania</option>
          <option value="NL">Países Bajos</option>
          <option value="PT">Portugal</option>
          <option value="GB">Reino Unido</option>
          <option value="MX">México</option>
          <option value="BR">Brasil</option>
          <option value="US">Estados Unidos</option>
          <option value="BE">Bélgica</option>
        </select>

        <select v-model="filters.seniority" class="bb-form nps-filters__select" @change="loadStats">
          <option value="">Todas las antigüedades</option>
          <option value="0-1">Menos de 1 mes</option>
          <option value="1-3">1 a 3 meses</option>
          <option value="3-12">3 a 12 meses</option>
          <option value="12+">Más de 12 meses</option>
        </select>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="nps-loading">Cargando datos…</div>

      <!-- Error -->
      <div v-else-if="error" class="nps-error">Error al cargar los datos.</div>

      <template v-else-if="stats">

        <!-- Hero: score + distribución -->
        <div class="nps-hero">
          <div class="nps-score-card">
            <div class="nps-score-card__value" :class="scoreClass">{{ stats.score }}</div>
            <div class="nps-score-card__label">NPS Score</div>
            <div class="nps-score-card__total">{{ stats.totalResponses }} respuestas</div>
          </div>

          <div class="nps-distribution-card">
            <div class="nps-distribution__bar">
              <div
                class="nps-distribution__segment nps-distribution__segment--promoter"
                :style="{ width: stats.promoters.percentage + '%' }"
              >{{ stats.promoters.percentage }}%</div>
              <div
                class="nps-distribution__segment nps-distribution__segment--passive"
                :style="{ width: stats.passives.percentage + '%' }"
              >{{ stats.passives.percentage }}%</div>
              <div
                class="nps-distribution__segment nps-distribution__segment--detractor"
                :style="{ width: stats.detractors.percentage + '%' }"
              >{{ stats.detractors.percentage }}%</div>
            </div>
            <div class="nps-distribution__legend">
              <span class="nps-distribution__legend-item nps-distribution__legend-item--promoter">
                Promotores (9-10) · {{ stats.promoters.count }}
              </span>
              <span class="nps-distribution__legend-item nps-distribution__legend-item--passive">
                Pasivos (7-8) · {{ stats.passives.count }}
              </span>
              <span class="nps-distribution__legend-item nps-distribution__legend-item--detractor">
                Detractores (0-6) · {{ stats.detractors.count }}
              </span>
            </div>
          </div>
        </div>

        <!-- Gráficos -->
        <div class="nps-charts">
          <div class="nps-chart-card">
            <h3 class="nps-chart-card__title">Distribución de puntuaciones</h3>
            <canvas ref="histogramChart" height="180"></canvas>
          </div>
          <div class="nps-chart-card">
            <h3 class="nps-chart-card__title">Tendencia NPS</h3>
            <canvas ref="trendChart" height="180"></canvas>
          </div>
        </div>

        <!-- Respuestas cualitativas -->
        <div class="nps-responses">
          <h3 class="nps-responses__title">Respuestas cualitativas</h3>
          <div class="nps-responses__tabs">
            <button
              v-for="tab in responseTabs"
              :key="tab.value"
              :class="['nps-responses__tab', { 'is-active': responseFilter === tab.value }]"
              @click="responseFilter = tab.value"
            >{{ tab.label }}</button>
          </div>

          <div v-if="filteredResponses.length === 0" class="nps-responses__empty">
            No hay respuestas para este segmento.
          </div>

          <div
            v-for="r in filteredResponses"
            :key="r.id"
            class="nps-response-item"
          >
            <div class="nps-response-item__header">
              <span :class="['nps-response-item__score', scoreSegmentClass(r.score)]">
                {{ r.score }}
              </span>
              <span class="nps-response-item__meta">{{ r.country }} · {{ seniorityLabel(r.seniority) }} · {{ formatDate(r.createdAt) }}</span>
            </div>
            <p class="nps-response-item__text">{{ r.qualitativeText }}</p>
          </div>
        </div>

      </template>
    </div>
  </section>
</template>

<script>
import Chart from 'chart.js';

export default {
  name: 'AdminNpsPage',

  data() {
    return {
      loading: false,
      error: false,
      stats: null,
      responseFilter: 'all',
      filters: {
        days: '30',
        country: '',
        seniority: '',
      },
      responseTabs: [
        { value: 'all', label: 'Todas' },
        { value: 'promoter', label: 'Promotores' },
        { value: 'passive', label: 'Pasivos' },
        { value: 'detractor', label: 'Detractores' },
      ],
    };
  },

  computed: {
    scoreClass() {
      if (this.stats.score >= 50) return 'is-great';
      if (this.stats.score >= 0) return 'is-ok';
      return 'is-bad';
    },

    filteredResponses() {
      if (!this.stats?.responses) return [];
      return this.stats.responses.filter(r => {
        if (this.responseFilter === 'promoter') return r.score >= 9;
        if (this.responseFilter === 'passive') return r.score >= 7 && r.score <= 8;
        if (this.responseFilter === 'detractor') return r.score <= 6;
        return true;
      }).filter(r => !!r.qualitativeText);
    },
  },

  methods: {
    async loadStats() {
      this.loading = true;
      this.error = false;
      this.destroyCharts();
      try {
        this.stats = await this.$store.dispatch('getNpsStats', this.filters);
      } catch {
        this.error = true;
      } finally {
        this.loading = false;
      }
      if (this.stats) {
        this.$nextTick(() => this.renderCharts());
      }
    },

    renderCharts() {
      this.renderHistogram();
      this.renderTrend();
    },

    renderHistogram() {
      const ctx = this.$refs.histogramChart;
      if (!ctx || !this.stats?.distribution) return;

      const colors = this.stats.distribution.map((_, i) => {
        if (i >= 9) return '#22c55e';
        if (i >= 7) return '#f59e0b';
        return '#ef4444';
      });

      this._histogram = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: ['0','1','2','3','4','5','6','7','8','9','10'],
          datasets: [{
            data: this.stats.distribution,
            backgroundColor: colors,
          }],
        },
        options: {
          legend: { display: false },
          scales: {
            yAxes: [{ ticks: { beginAtZero: true, stepSize: 1 } }],
          },
        },
      });
    },

    renderTrend() {
      const ctx = this.$refs.trendChart;
      if (!ctx || !this.stats?.trend?.length) return;

      this._trend = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.stats.trend.map(t => t.period),
          datasets: [{
            data: this.stats.trend.map(t => t.score),
            borderColor: '#ffc944',
            backgroundColor: 'rgba(255, 201, 68, 0.1)',
            lineTension: 0.3,
            fill: true,
            pointBackgroundColor: '#ffc944',
          }],
        },
        options: {
          legend: { display: false },
          scales: {
            yAxes: [{ ticks: { min: -100, max: 100 } }],
          },
        },
      });
    },

    destroyCharts() {
      this._histogram?.destroy();
      this._trend?.destroy();
      this._histogram = null;
      this._trend = null;
    },

    scoreSegmentClass(score) {
      if (score >= 9) return 'is-promoter';
      if (score >= 7) return 'is-passive';
      return 'is-detractor';
    },

    seniorityLabel(seniority) {
      const map = {
        '0-1': '< 1 mes',
        '1-3': '1-3 meses',
        '3-12': '3-12 meses',
        '12+': '> 12 meses',
      };
      return map[seniority] || seniority;
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
    },
  },

  mounted() {
    this.loadStats();
  },

  beforeDestroy() {
    this.destroyCharts();
  },
};
</script>

<style scoped>
.admin-nps-page {
  padding: 32px 0 64px;
}

/* Filtros */
.nps-filters {
  display: flex;
  gap: 12px;
  margin-bottom: 32px;
  flex-wrap: wrap;
}
.nps-filters__select {
  min-width: 180px;
}

/* Loading / Error */
.nps-loading,
.nps-error {
  padding: 48px;
  text-align: center;
  color: #999;
  font-size: 15px;
}

/* Hero */
.nps-hero {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 24px;
  margin-bottom: 32px;
  align-items: center;
}

.nps-score-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
}
.nps-score-card__value {
  font-size: 56px;
  font-weight: 800;
  line-height: 1;
}
.nps-score-card__value.is-great { color: #16a34a; }
.nps-score-card__value.is-ok    { color: #d97706; }
.nps-score-card__value.is-bad   { color: #dc2626; }
.nps-score-card__label {
  font-size: 13px;
  color: #999;
  margin-top: 6px;
}
.nps-score-card__total {
  font-size: 13px;
  color: #666;
  margin-top: 4px;
}

.nps-distribution-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 24px;
}
.nps-distribution__bar {
  display: flex;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  gap: 2px;
}
.nps-distribution__segment {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  transition: width 0.4s ease;
  min-width: 30px;
}
.nps-distribution__segment--promoter  { background: #22c55e; }
.nps-distribution__segment--passive   { background: #f59e0b; }
.nps-distribution__segment--detractor { background: #ef4444; }

.nps-distribution__legend {
  display: flex;
  gap: 20px;
  margin-top: 14px;
  flex-wrap: wrap;
}
.nps-distribution__legend-item {
  font-size: 13px;
  color: #555;
  display: flex;
  align-items: center;
  gap: 6px;
}
.nps-distribution__legend-item::before {
  content: '';
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}
.nps-distribution__legend-item--promoter::before  { background: #22c55e; }
.nps-distribution__legend-item--passive::before   { background: #f59e0b; }
.nps-distribution__legend-item--detractor::before { background: #ef4444; }

/* Charts */
.nps-charts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
}
.nps-chart-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 24px;
}
.nps-chart-card__title {
  font-size: 14px;
  font-weight: 600;
  color: #444;
  margin: 0 0 16px;
}

/* Responses */
.nps-responses {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 24px;
}
.nps-responses__title {
  font-size: 14px;
  font-weight: 600;
  color: #444;
  margin: 0 0 16px;
}
.nps-responses__tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}
.nps-responses__tab {
  background: none;
  border: 1.5px solid #d0d0d0;
  border-radius: 50px;
  padding: 6px 16px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  transition: all 0.15s;
}
.nps-responses__tab.is-active {
  border-color: #1e0028;
  background: #1e0028;
  color: #fff;
}
.nps-responses__empty {
  color: #999;
  font-size: 14px;
  padding: 24px 0;
}

.nps-response-item {
  border-top: 1px solid #f0f0f0;
  padding: 16px 0;
}
.nps-response-item__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}
.nps-response-item__score {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  color: #fff;
  flex-shrink: 0;
}
.nps-response-item__score.is-promoter  { background: #22c55e; }
.nps-response-item__score.is-passive   { background: #f59e0b; }
.nps-response-item__score.is-detractor { background: #ef4444; }
.nps-response-item__meta {
  font-size: 12px;
  color: #999;
}
.nps-response-item__text {
  font-size: 14px;
  color: #333;
  margin: 0;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .nps-hero {
    grid-template-columns: 1fr;
  }
  .nps-charts {
    grid-template-columns: 1fr;
  }
}
</style>
